import { useState, useCallback, useMemo, useRef, useEffect, useLayoutEffect } from "preact/hooks";
import md5 from "blueimp-md5";
import { ASSET_ROOT } from "../lib/constants";
import { getModalComponent } from "./modalRegistry";
import { ModalContext, type ModalActions } from "./ModalContext";
import { getDomElement, fadeIn, fadeOut } from "../utils/dom";
import { refreshartistdashboard } from "../features/gallery";
import { modalPostLoad } from "./modalPostLoad";
import {
  setModalOpen,
  setLastModalOpen,
  setModalOpenWhat,
  setPremodalUrl,
  open_modal_id,
  open_modal_url,
  open_modal_what,
  open_modal_size,
  open_modal_title,
} from "../globalState";

// ── Types ────────────────────────────────────────────────────

interface ModalEntry {
  id: string;
  what: string;
  parts: string[];
  size: string;
  url: string;
  zIndex: number; // backdrop
  zIndex2: number; // modal
}

export interface ModalStackHandle {
  open: (what: string, size?: string, swap?: number) => void;
  closeLast: () => void;
  close: (modalId: string) => void;
  refresh: () => void;
  closeLastRefresh: () => void;
  getDepth: () => number;
  getCurrentWhat: () => string;
}

// ── Singleton ref exposed for imperative bridge ──────────────

let _handle: ModalStackHandle | null = null;
export function getModalStackHandle(): ModalStackHandle | null {
  return _handle;
}

/**
 * Where to send the address bar when the whole stack closes, when the page was ENTERED on a
 * modal URL (`/view/3676508` — see views/routing/modalRoute.tsx).
 *
 * `open()` normally remembers `location.pathname`, which is right when a modal is opened
 * from a page. On a cold load of a modal URL that pathname IS the modal, so closing would
 * leave the reader on a URL with no page behind it — one that reopens the modal on reload.
 * The server names the page it rendered underneath and `pageInit` seeds it here first.
 *
 * Module-level and consumed once: it describes how this document was entered, so it must
 * not survive into a modal the reader opens later from the restored page.
 */
let pendingReturnUrl: string | null = null;

/** Seed the close-target before the first `modal()` of a cold load. See `pendingReturnUrl`. */
export function setPendingReturnUrl(url: string): void {
  pendingReturnUrl = url;
}

// ── Z-index counter (module-level, never resets) ─────────────

let nextZ = 1000;
function allocZ(): { z1: number; z2: number } {
  nextZ += 2;
  return { z1: nextZ, z2: nextZ + 1 };
}

// ── URL helpers ──────────────────────────────────────────────

function urlUpdate(url: string, replace: boolean): void {
  if (replace) {
    window.history.replaceState(null, "", url);
  } else {
    window.history.pushState(null, "", url);
  }
}

function whatToUrl(what: string): string {
  return "/" + what.replace(/:/g, "/");
}

/**
 * Top of the modal stack, or undefined when empty.
 *
 * Every read of `stack[stack.length - 1]` in this file already sits behind a
 * length check, but those checks do not narrow an index expression, so under
 * `noUncheckedIndexedAccess` each one needed a guard. Going through one helper
 * keeps the guard honest without restructuring the control flow.
 */
function peek<T>(stack: T[]): T | undefined {
  return stack[stack.length - 1];
}

// ── Close-modal button (stays in chrome.php #closemodal) ─────

let closeModalButtonTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Pending body-scroll lock. The lock is applied 1s AFTER a modal opens (legacy behaviour), so a
 * modal opened and closed inside that second used to leave the timer running: close cleared
 * `overflow`, then the orphaned timer set it back to "hidden" with no modal on screen. The page
 * could not be scrolled again until a reload — which is exactly what rapid open/close of
 * artworks on the search page produced. Cancelling it on close is what makes the pair safe.
 */
let scrollLockTimer: ReturnType<typeof setTimeout> | null = null;

function syncCloseButton(stack: ModalEntry[]): void {
  const closeEl = getDomElement("closemodal");
  if (!closeEl) return;

  if (stack.length === 0) {
    closeEl.style.display = "none";
    return;
  }

  closeEl.style.display = "none";
  if (closeModalButtonTimer) clearTimeout(closeModalButtonTimer);
  closeModalButtonTimer = setTimeout(() => {
    fadeIn(closeEl);
  }, 800);

  // Remove all size classes
  closeEl.classList.remove(
    "closemodal-size-dynamic",
    "closemodal-size-large",
    "closemodal-size-medium",
    "closemodal-size-small",
    "closemodal-size-full",
    "closemodal-white"
  );

  const top = peek(stack);
  if (!top) return;
  if (top.size === "full") {
    closeEl.classList.add("closemodal-size-full", "closemodal-white");
  } else {
    closeEl.classList.add(`closemodal-size-${top.size}`);
  }
}

// ── ModalStack component ─────────────────────────────────────

export function ModalStack() {
  const [stack, setStack] = useState<ModalEntry[]>([]);

  // Keep pre-modal state in a ref (not React state — we only read on close)
  const preModalRef = useRef({ url: "", title: "" });

  // Expose depth + currentWhat for bridge reads (avoid stale closures)
  const stackRef = useRef(stack);
  stackRef.current = stack;

  // ── open ────────────────────────────────────────────────────

  const open = useCallback((what: string, size?: string, swap?: number) => {
    // Hide newsletter preview if visible
    const nlPreview = getDomElement("editnewsletter-preview");
    if (nlPreview) nlPreview.style.display = "none";

    console.log("*** Loading modal " + what);
    const parts = what.split(":");
    const url = whatToUrl(what);

    setStack((prev) => {
      if (swap === 1 && prev.length > 0) {
        // Swap: update the top entry in-place (keep same id to avoid remount flicker)
        const updated = [...prev];
        const prevTop = peek(updated);
        if (!prevTop) return prev;
        const top = {
          ...prevTop,
          what,
          parts,
          size: size ?? "dynamic",
          url,
        };
        updated[updated.length - 1] = top;
        urlUpdate(url, true);
        // Signal Preact modal components to refetch their data
        window.dispatchEvent(new CustomEvent("modal-refresh"));
        return updated;
      }

      // New modal
      const id = md5(what);
      const { z1, z2 } = allocZ();
      const entry: ModalEntry = {
        id,
        what,
        parts,
        size: size || "small",
        url,
        zIndex: z1,
        zIndex2: z2,
      };

      if (prev.length === 0) {
        preModalRef.current = {
          url: pendingReturnUrl ?? window.location.pathname,
          title: document.title,
        };
        pendingReturnUrl = null;
        // `replace`, not `push`, when the document was entered on this very URL: the modal
        // is already what the address bar says, so pushing would make Back a no-op that
        // reopens it. The return url is what Back should reach instead.
        urlUpdate(url, url === window.location.pathname);
      } else {
        urlUpdate(url, true);
      }

      setTimeout(() => {
        const closeEl = getDomElement("closemodal");
        if (closeEl) fadeIn(closeEl);
      }, 400);

      const next = [...prev, entry];

      // Lock body scroll after a short delay (matches legacy behavior).
      if (scrollLockTimer) clearTimeout(scrollLockTimer);
      scrollLockTimer = setTimeout(() => {
        scrollLockTimer = null;
        document.body.style.overflow = "hidden";
      }, 1000);

      return next;
    });
  }, []);

  // ── close ───────────────────────────────────────────────────

  const close = useCallback((modalId: string) => {
    const nlPreview = getDomElement("editnewsletter-preview");
    if (nlPreview) nlPreview.style.display = "none";

    setStack((prev) => {
      const idx = prev.findIndex((e) => e.id === modalId);
      if (idx === -1) return prev;

      const closing = prev[idx];
      if (!closing) return prev;
      const next = prev.filter((_, i) => i !== idx);

      // Fade out backdrop
      const backEl = getDomElement(`modal_back_${closing.id}`);
      if (backEl) fadeOut(backEl);

      // Auto-refresh parent for specific modal types
      if (next.length > 0) {
        const parentWhat = peek(next)?.what ?? "";
        const parentSlug = parentWhat.split(":")[0];
        if (
          parentWhat === "newsletters" ||
          parentWhat === "newsletters:artists:" ||
          parentWhat === "newsletters:welcome:" ||
          parentSlug === "directoryedit" ||
          parentSlug === "briefs"
        ) {
          setTimeout(() => {
            const handle = getModalStackHandle();
            handle?.refresh();
          }, 1000);
        }

        // Show newsletter preview when returning to newsletter editor
        if (parentSlug === "editnewsletter" || parentSlug === "editnewsletter-artist") {
          setTimeout(() => {
            const preview = getDomElement("editnewsletter-preview");
            if (preview) fadeIn(preview);
          }, 500);
        }
      }

      // Refresh artist dashboard if visible
      if (getDomElement("ad_name-1")) {
        void refreshartistdashboard();
      }

      if (closeModalButtonTimer) clearTimeout(closeModalButtonTimer);

      if (next.length === 0) {
        // Restore URL + title
        const { url, title } = preModalRef.current;
        if (title) document.title = title;
        if (url) urlUpdate(url, true);
        // Cancel any lock still pending from the open that is being undone here.
        if (scrollLockTimer) {
          clearTimeout(scrollLockTimer);
          scrollLockTimer = null;
        }
        document.body.style.overflow = "";
        const closeEl = getDomElement("closemodal");
        if (closeEl) closeEl.style.display = "none";
      } else {
        const newTop = peek(next);
        if (newTop) urlUpdate(newTop.url, true);
        syncCloseButton(next);
      }

      return next;
    });
  }, []);

  // ── closeLast ───────────────────────────────────────────────

  const closeLast = useCallback(() => {
    const s = stackRef.current;
    const top = peek(s);
    if (top) close(top.id);
  }, [close]);

  // ── refresh ─────────────────────────────────────────────────

  const refresh = useCallback(() => {
    const s = stackRef.current;
    const top = peek(s);
    if (top) open(top.what, top.size, 1);
  }, [open]);

  // ── closeLastRefresh ────────────────────────────────────────

  const closeLastRefresh = useCallback(() => {
    setTimeout(refresh, 500);
    closeLast();
  }, [closeLast, refresh]);

  // ── context value ───────────────────────────────────────────

  const ctx = useMemo<ModalActions>(
    () => ({
      open,
      closeLast,
      close,
      refresh,
      closeLastRefresh,
      get depth() {
        return stackRef.current.length;
      },
      get currentWhat() {
        const s = stackRef.current;
        return peek(s)?.what ?? "";
      },
    }),
    [open, closeLast, close, refresh, closeLastRefresh]
  );

  // ── Expose imperative handle ────────────────────────────────

  useLayoutEffect(() => {
    _handle = {
      open,
      closeLast,
      close,
      refresh,
      closeLastRefresh,
      getDepth: () => stackRef.current.length,
      getCurrentWhat: () => {
        const s = stackRef.current;
        return peek(s)?.what ?? "";
      },
    };
    return () => {
      _handle = null;
    };
  }, [open, closeLast, close, refresh, closeLastRefresh]);

  // ── Sync close button whenever stack changes ────────────────

  useEffect(() => {
    syncCloseButton(stack);
  }, [stack]);

  // ── Sync globalState for external readers ───────────────────

  useEffect(() => {
    // Keep globalState in sync for legacy readers
    // (eventHandlers.ts, contentLoader.ts, miscOperations.ts)
    setModalOpen(stack.length);
    setLastModalOpen(peek(stack)?.id ?? "");
    setModalOpenWhat(peek(stack)?.what ?? "");
    setPremodalUrl(preModalRef.current.url);

    // Sync the legacy arrays (mutate in place)
    open_modal_id.length = 0;
    open_modal_url.length = 0;
    open_modal_what.length = 0;
    open_modal_size.length = 0;
    open_modal_title.length = 0;
    open_modal_id.push("");
    open_modal_url.push("");
    open_modal_what.push("");
    open_modal_size.push("");
    open_modal_title.push("");
    for (const entry of stack) {
      open_modal_id.push(entry.id);
      open_modal_url.push(entry.url);
      open_modal_what.push(entry.what);
      open_modal_size.push(entry.size);
      open_modal_title.push(document.title);
    }
  }, [stack]);

  // ── Render ──────────────────────────────────────────────────

  return (
    <ModalContext.Provider value={ctx}>
      {stack.map((entry) => (
        <ModalLayer key={entry.id} entry={entry} onClose={closeLast} onRefresh={refresh} />
      ))}
    </ModalContext.Provider>
  );
}

// ── Individual modal layer ───────────────────────────────────

interface ModalLayerProps {
  entry: ModalEntry;
  onClose: () => void;
  onRefresh: () => void;
}

function ModalLayer({ entry, onClose, onRefresh }: ModalLayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [Component, setComponent] = useState<preact.ComponentType<{
    parts: string[];
    onClose: () => void;
    onRefresh: () => void;
  }> | null>(null);
  const [loading, setLoading] = useState(true);

  // Load the component
  useEffect(() => {
    setLoading(true);
    setComponent(null);

    // split(":") always yields at least one element, but the compiler cannot know that.
    const slug = entry.parts[0] ?? "";
    const loader = getModalComponent(slug);
    if (!loader) {
      console.error("Unknown modal slug:", slug);
      setLoading(false);
      return;
    }

    loader()
      .then(({ default: Comp }) => {
        setComponent(() => Comp);
        setLoading(false);
      })
      .catch((err: unknown) => {
        console.error("Failed to load modal component:", err);
        setLoading(false);
      });
  }, [entry.what, entry.parts[0]]);

  // Fade in backdrop on mount
  useEffect(() => {
    const backEl = getDomElement(`modal_back_${entry.id}`);
    if (backEl) fadeIn(backEl);
  }, [entry.id]);

  // Run modalPostLoad after component renders
  useEffect(() => {
    if (Component && !loading) {
      modalPostLoad(entry.parts);
    }
  }, [Component, loading, entry.parts]);

  const handleBackdropClick = useCallback(() => {
    // Use the ModalStack close to close by id
    const handle = getModalStackHandle();
    if (handle) handle.close(entry.id);
  }, [entry.id]);

  return (
    <>
      <div
        class="modal_back"
        id={`modal_back_${entry.id}`}
        style={{ display: "none", zIndex: entry.zIndex }}
        onClick={handleBackdropClick}
      />
      <div
        class={`modal modal-size-${entry.size}`}
        id={`modal_${entry.id}`}
        style={{ zIndex: entry.zIndex2 }}
        ref={containerRef}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "200px",
              width: "100%",
              height: "100%",
            }}
          >
            <div class="loader">
              <img src={`${ASSET_ROOT}assets/img/pictufy-icon.png`} />
            </div>
          </div>
        ) : Component ? (
          <Component parts={entry.parts} onClose={onClose} onRefresh={onRefresh} />
        ) : (
          <div class="modal_inner">
            <div class="modal_header">Error</div>
            <p>Unknown modal: {entry.parts[0]}</p>
          </div>
        )}
      </div>
    </>
  );
}
